import { useDesktopMode } from "@/stores/desktop-mode-store";
import { isTauri } from "@/lib/is-tauri";
import { getWindowRoleSync } from "@/desktop/use-window-role";
import { usePresentationStore } from "@/stores/presentation-store";

export function shouldUseDesktopExpandedPresentation(): boolean {
  return isTauri();
}

/** Whether this window shows a Focus it enters; mirrors only when allowed. */
export function presentsDesktopFocusHere(allowMirror?: boolean): boolean {
  return !!allowMirror || getWindowRoleSync() === "owner";
}

/**
 * Whether this window shows a preview: focus drawn above the wall while the
 * Workspace stays open (a card click, Part 05 Q1). Focus mode instead lets
 * the Workspace step aside.
 */
export function previewOpenHere(): boolean {
  const presentation = usePresentationStore.getState().presentation;
  return (
    shouldUseDesktopExpandedPresentation() &&
    presentation?.variant === "focus" &&
    presentation.renderer === "overlay" &&
    useDesktopMode.getState().mode === "denk"
  );
}

/**
 * Places presented frames on the desktop. `preview` says where a focus
 * entry shows: `true` above the wall in the Workspace, `false` in focus
 * mode (the Workspace steps aside and the frames float over the desktop,
 * Back returns to the wall). Omitted, an open preview stays one.
 */
export function focusPresentedFramesInDesktopMode(
  frameIds: string[],
  options: {
    allowMirror?: boolean;
    returnToWorkspace?: boolean;
    preview?: boolean;
  } = {},
) {
  if (!presentsDesktopFocusHere(options.allowMirror)) return;

  const desktopMode = useDesktopMode.getState();
  if (usePresentationStore.getState().presentation?.variant !== "focus") {
    if (desktopMode.mode !== "denk") {
      desktopMode.openDenkMode();
    }
    if (useDesktopMode.getState().mode === "denk") {
      useDesktopMode.getState().setDenkCanvasScope("single-monitor", frameIds);
    }
    return;
  }
  if (!shouldUseDesktopExpandedPresentation()) return;
  if (
    options.preview !== false &&
    usePresentationStore.getState().presentation?.renderer === "overlay" &&
    desktopMode.mode === "denk"
  ) {
    // A preview opens above the wall inside the Workspace (Part 05 Q1), on
    // this display only.
    if (desktopMode.denkCanvasScope !== "single-monitor") {
      desktopMode.setDenkCanvasScope("single-monitor");
    }
    return;
  }
  const returnToWorkspace =
    options.returnToWorkspace ??
    (desktopMode.compactFrameOverlayReturnsToWorkspace ||
      desktopMode.mode === "denk");
  desktopMode.closeWorkspace(frameIds, { returnToWorkspace });
}
